/*
 * @providesModule TournamentStore
   
 */

import MinFlux from 'min-flux';
import PlayableMatchesStore from 'PlayableMatchesStore';
import RESTStore from 'RESTStore';
import Tournament from 'SKZTournament';
import _ from 'lodash';

import {NativeModules, Platform} from 'react-native';

const SKZAPIManager = NativeModules.SKZAPIManager;

const TournamentStore = new MinFlux.Store({
  getData(tournamentID: any): Object {
    return this._data;
  },

  getTournamentByMatchCode(matchCode: string): Promise {
    const retryPrompt = Platform.OS == 'android' ? true : SKZAPIManager.OnError.promptRetry;
    return new Promise((resolve: any, reject: any) => {
      RESTStore.getServerResponse(
        'tournaments/',
        {matchCode},
        retryPrompt,
        (response: Object, error: string) => {
          if (error) {
            reject();
          } else if (response && response instanceof Array && response.length && response[0].id) {
            const tournament = new Tournament(response[0]);
            resolve(tournament);
          } else {
            reject();
          }
        }
      );
    });
  },

  getTournamentById(tournamentId, shouldBypassCache: boolean): Promise {
    // Guard against undefined/null tournamentId to prevent malformed URLs like "tournaments/undefined"
    if (tournamentId === undefined || tournamentId === null || tournamentId === '') {
      console.warn(
        `TournamentStore.getTournamentById: Invalid tournamentId (${tournamentId}). Rejecting promise to prevent malformed API request.`
      );
      return Promise.reject(
        new Error(`Invalid tournamentId: ${tournamentId}. Cannot fetch tournament.`)
      );
    }

    const retryPrompt = Platform.OS == 'android' ? true : SKZAPIManager.OnError.promptRetry;
    return new Promise((resolve, reject) => {
      if (shouldBypassCache) {
        RESTStore.getServerResponse(
          'tournaments/' + tournamentId,
          null,
          retryPrompt,
          (response: Object, error: string) => {
            if (error) {
              reject();
            } else {
              const tournament = new Tournament(response);
              resolve(tournament);
            }
          }
        );
      } else {
        const tournamentFromStore = PlayableMatchesStore.getTournamentById(tournamentId);
        if (tournamentFromStore) {
          const storeTournament = new Tournament(tournamentFromStore);
          resolve(storeTournament);
        } else {
          RESTStore.getAnyResponse(
            'tournaments/' + tournamentId,
            null,
            retryPrompt,
            (response: Object, error: string) => {
              if (error) {
                reject();
              } else {
                const tournament = new Tournament(response);
                resolve(tournament);
              }
            }
          );
        }
      }
    });
  },

  synthesizeTurnBasedMatchInfo(match: Object, tournament: Object, userId: any): Object {
    const tbMatchInfo = {};
    const player = this.getPlayer(match, userId);

    tbMatchInfo.cMatchStartDate = match.started_at;
    tbMatchInfo.cUserUniqueId = String(player.user_id);
    tbMatchInfo.cUserAvatarURL = player.thumbnail_url;
    tbMatchInfo.cUserDisplayName = player.name;
    tbMatchInfo.cUserCurrentTotalScore = String(player.score);
    tbMatchInfo.cUserFlagUrl = player.flag_url;

    const opponent = this.getOpponent(match, Number(userId));

    if (opponent) {
      tbMatchInfo.cOpponentUniqueId = String(opponent.user_id);
      tbMatchInfo.cOpponentAvatarURL = opponent.thumbnail_url;
      tbMatchInfo.cOpponentDisplayName = opponent.name;
      tbMatchInfo.cOpponentCurrentTotalScore = String(opponent.score);
      tbMatchInfo.cOpponentFlagUrl = opponent.flag_url;
    }

    tbMatchInfo.cPrize = tournament.total_cash_prize;
    tbMatchInfo.cPrizePoints = tournament.total_z_prize;
    tbMatchInfo.cTournamentId = Number(tournament.id);
    tbMatchInfo.cGameData = JSON.stringify(match.rules);
    tbMatchInfo.cIsGameCompleted = match.category == 'COMPLETED';

    tbMatchInfo.cDescription = tournament.description;
    tbMatchInfo.cEntryFee = tournament.cash_fee;
    tbMatchInfo.cEntryPoints = tournament.z_fee;
    tbMatchInfo.cName = tournament.name;
    tbMatchInfo.isPointsTournament = tournament.z_fee;
    return tbMatchInfo;
  },

  getPlayer: function (match: Object, userId: any): Object {
    return _.find(match.players, {user_id: Number(userId)}) || null;
  },

  getOpponent: function (match: Object, userId: any): Object {
    return _.first(_.reject(match.players, 'user_id', Number(userId))) || null;
  },

  actionTypeHandlers: {
    FETCH_TOURNAMENT(action: Object) {
      const tournamentID = action.payload?.tournamentID;
      // Guard against undefined/null tournamentID to prevent malformed URLs like "tournaments/undefined"
      if (tournamentID === undefined || tournamentID === null || tournamentID === '') {
        console.warn(
          `TournamentStore.FETCH_TOURNAMENT: Invalid tournamentID (${tournamentID}). Skipping API request to prevent malformed URL.`
        );
        return;
      }
      RESTStore.getAnyResponse(
        'tournaments/' + tournamentID,
        null,
        SKZAPIManager.OnError.promptRetry,
        (response: Object) => {
          this._data = response;
          this.emitChange();
        }
      );
    },

    JOIN_TOURNAMENT(action: Object) {
      console.debug('attempt join' + action.payload.tournamentID);
    },
  },
});

export default TournamentStore;
